import React, { useMemo } from 'react'
import { useRouteMatch } from 'react-router-dom'
import {
  RouteParams,
  VersionedRouteKeys,
  getAllTemplatesForVersionedRoute,
  routes,
} from '@sketch/modules-common'

import {
  ButtonUnstyled,
  SFSymbolLabel,
  useResponsiveDropdown,
} from '@sketch/components'

import { FrameGroupDetailsFragment } from '@sketch/gql-types'

import { ShareWithoutVersion } from '../../../../versioning'

import { PROTOTYPES_TEXT, PAGES_TEXT, COMPONENTS_TEXT } from './constants'
import { ChevronsIcon } from '../NavigationItem.styles'
import NavigationItem, { icons } from '../NavigationItem'
import { PagesDropdown } from '../../PagesDropdown'
import { HeaderNavigationSectionInfo } from '../types'

interface PagesNavigationItemProps {
  share: ShareWithoutVersion
  frameGroup?: FrameGroupDetailsFragment
  sectionInfo?: HeaderNavigationSectionInfo
}

interface PagenameAndIcon {
  icon: keyof typeof icons
  pageName: React.ReactNode
}

const DROPDOWN_STYLE = {
  maxHeight: '80vh',
  overflowY: 'auto',
} as const

const NAVIGATION_ITEM_PROPS: Record<string, (arg?: string) => PagenameAndIcon> =
  {
    artboard: pageName => ({
      pageName: <>{pageName && <SFSymbolLabel text={pageName} />}</>,
      icon: 'page',
    }),
    prototype: () => ({
      pageName: <>{PROTOTYPES_TEXT}</>,
      icon: 'prototype',
    }),
    page: () => ({
      pageName: <>{PAGES_TEXT}</>,
      icon: 'page',
    }),
    document: () => ({
      pageName: <>{PAGES_TEXT}</>,
      icon: 'page',
    }),
    components: () => ({
      pageName: <>{COMPONENTS_TEXT}</>,
      icon: 'components',
    }),
    default: () => ({
      pageName: <></>,
      icon: 'page',
    }),
  }

// Route keys grouped by which NAVIGATION_ITEM_PROPS variant they map to.
// Built from getAllTemplatesForVersionedRoute so adding a new route to a
// group can't accidentally omit the LATEST or VERSION variant.
const getPagenameAndIcon = (
  path: string,
  frameName: string
): PagenameAndIcon => {
  const routeGroups: [VersionedRouteKeys[], () => PagenameAndIcon][] = [
    [
      ['ARTBOARD_DETAIL', 'FRAME', 'PAGE_SECTION'],
      () => NAVIGATION_ITEM_PROPS.artboard(frameName),
    ],
    [['SHARE_PROTOTYPES'], () => NAVIGATION_ITEM_PROPS.prototype()],
    [
      ['SHARE_PAGE_VIEW', 'SHARE_PAGE_CANVAS_VIEW'],
      () => NAVIGATION_ITEM_PROPS.page(),
    ],
    [['SHARE_VIEW'], () => NAVIGATION_ITEM_PROPS.document()],
    [
      [
        'CWV_COLOR_VARIABLES',
        'CWV_LAYER_STYLES',
        'CWV_SYMBOLS',
        'CWV_TEXT_STYLES',
      ],
      () => NAVIGATION_ITEM_PROPS.components(),
    ],
  ]

  for (const [routeKeys, getProps] of routeGroups) {
    const isMatch = routeKeys.some(routeKey =>
      getAllTemplatesForVersionedRoute(routeKey).includes(path)
    )

    if (isMatch) {
      return getProps()
    }
  }

  return NAVIGATION_ITEM_PROPS.default()
}

const PagesNavigationItem: React.FCC<PagesNavigationItemProps> = ({
  frameGroup,
  sectionInfo,
  share,
}) => {
  const match = useRouteMatch<RouteParams<'SHARE_PAGE_VIEW'>>()

  const [content, buttonProps] = useResponsiveDropdown({
    dropdown: PagesDropdown,
    dropdownProps: {
      shareIdentifier: share.identifier,
    },
    dropdownStyle: DROPDOWN_STYLE,
    usePortal: true,
  })

  const focusedPageName = frameGroup?.page?.name || sectionInfo?.pageName || ''
  const focusedPageUUID = frameGroup?.page?.uuid || sectionInfo?.pageUUID

  const { icon, pageName } = useMemo(
    () => getPagenameAndIcon(match.path, focusedPageName),
    [match.path, focusedPageName]
  )

  if (frameGroup || sectionInfo) {
    return (
      <NavigationItem
        to={routes.SHARE_PAGE_CANVAS_VIEW.create({
          shareID: share.identifier,
          pageUUID: focusedPageUUID!,
        })}
        icon={icon}
        label={pageName}
        secondary
      />
    )
  }

  return (
    <>
      <ButtonUnstyled data-testid="page-selector" {...buttonProps}>
        <NavigationItem icon={icon} label={pageName} />
        <ChevronsIcon />
      </ButtonUnstyled>
      {content}
    </>
  )
}

export default PagesNavigationItem
